// @flow

import * as React from 'react';
import {
  useRelayEnvironment,
  graphql,
  useFragment,
  QueryRenderer,
} from 'react-relay';
import dynamic from 'next/dynamic';
import { useRouter } from '../../controls/router';
import { useUserData } from '../../hooks/user-data';
import { useLocale } from '../../hooks/locale';
import { type Href, toPageHref } from '../../utils/rewrites';
import type { ListingsParams } from './listingsDescriptor';
import type { aggregatesListingDialogClientQueryResponse } from './__generated__/aggregatesListingDialogClientQuery.graphql';
import type { aggregatesListingDialogClient_aggregatesListings$key } from './__generated__/aggregatesListingDialogClient_aggregatesListings.graphql';

type Props = {|
  parentHref: Href,
  params: ListingsParams,
  aggregatesListings: aggregatesListingDialogClient_aggregatesListings$key,
|};

const AggregatesListingDialog = dynamic(() =>
  import('./aggregates-listing-dialog').then(m => m.AggregatesListingDialog),
);

const AggregatesListingPageMeta = dynamic(() =>
  import('./AggregatesListingPageMeta').then(m => m.AggregatesListingPageMeta),
);

const QUERY = graphql`
  query aggregatesListingDialogClientQuery($slug: String!, $lang: String!) {
    listing: aggregatesListingBySlug(slug: $slug) {
      ...aggregatesListingDialog_listing
      ...AggregatesListingPageMeta_listing
    }

    cms {
      listingPage: pageWithData(uid: "listing", lang: $lang) {
        page {
          ...AggregatesListingPageMeta_page
        }
      }
    }
  }
`;

export const AggregatesListingDialogClient = (props: Props): React.Node => {
  const router = useRouter();
  const { routes } = useUserData();
  const { language } = useLocale();
  const environment = useRelayEnvironment();

  const aggregatesListings = useFragment(
    graphql`
      fragment aggregatesListingDialogClient_aggregatesListings on AggregatesListingConnectionConnection {
        ...aggregatesListingDialog_aggregatesListings
      }
    `,
    props.aggregatesListings,
  );

  // For showing dialog and for rendering it serverside we use following nextjs trick
  // Link "as" property can be anything, all internal routing is depending on "href" attribute
  // So client side when clicking on Card we shallowly changing only "as" property
  // This allows us to not refetch listings and just show listing dialog based on "as" path
  // On refresh listing.page.js is rendered
  const asPathHref = React.useMemo(
    () => toPageHref(routes, router.asPath, language),
    [routes, router.asPath, language],
  );

  if (
    asPathHref.pathname === '/listing' &&
    asPathHref.query.listingSlug != null
  ) {
    return (
      <QueryRenderer
        environment={environment}
        query={QUERY}
        variables={{
          slug: JSON.parse(asPathHref.query.listingSlug),
          // On localhost we don't have lang in query, fallback to language
          lang: asPathHref.query.lang ?? language,
        }}
        fetchPolicy="store-and-network"
        render={({
          props: data,
        }: {
          props: ?aggregatesListingDialogClientQueryResponse,
          error: ?Error,
          ...
        }) => {
          return (
            <>
              {data?.listing ? (
                <AggregatesListingPageMeta
                  listing={data.listing}
                  page={data.cms.listingPage?.page}
                />
              ) : null}
              <AggregatesListingDialog
                listing={data?.listing}
                shallowCardsLink={true}
                replaceCardsLink={true}
                params={props.params}
                aggregatesListings={aggregatesListings}
                // Needed for cmd clicks etc
                backHref={{
                  pathname: router.pathname,
                  query: router.query,
                }}
                onClose={e => {
                  if (!(e.metaKey || e.ctrlKey)) {
                    // prevent original link to work
                    e.stopPropagation();
                    // we are on client, so here always have back pointing to listings
                    router.push(props.parentHref);
                  }
                }}
              />
            </>
          );
        }}
      />
    );
  }
  return null;
};
